import { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import dynamic from 'next/dynamic';

import { TourExtraServiceSearchViewModel } from '@api/index';
import { useBasketId } from '@hooks/cache';
import { callService } from '@api/remote-call';
import { ExtraServiceRequestType } from '@components/views/types';
import { useDevice } from '@contexts/device';

const TourExtraServiceFormDesktop = dynamic(
  () => import('./TourExtraServiceFormDesktop')
);

const TourExtraServiceFormMobile = dynamic(
  () => import('./TourExtraServiceFormMobile')
);

export type ExtraServiceInitialFormType = {
  tourId: number;
  itemId: string;
  startDate: string;
  endDate: string;
  saleDate: string;
  numberOfPeople: number;
  currency: string;
  tourPrice: number;
  data: TourExtraServiceSearchViewModel;
};

type ExtraServiceFormProps = {
  onCancel?(): void;
  onSuccess?(): void;
  initialValues: ExtraServiceInitialFormType;
};

const TourExtraServiceForm = ({
  onCancel,
  onSuccess,
  initialValues,
}: ExtraServiceFormProps) => {
  const { isDesktop } = useDevice();
  const [extraServiceForm, setExtraServiceForm] = useState<
    ExtraServiceRequestType[]
  >([]);

  const [selectedExtraServiceItems, setSelectedExtraServiceItems] = useState(
    initialValues?.data?.extraServices?.map((extraService) => ({
      extraServiceId: extraService.extraServiceId,
      quantity: 0,
    }))
  );
  const [, refresh] = useState({});
  const basketId = useBasketId();

  useEffect(() => {
    if (initialValues?.data?.extraServices) {
      const extraServiceFormData = initialValues.data.extraServices.map(
        (extraService) => ({
          id: extraService.extraServiceId,
          quantity: 1,
          numberOfPeople: initialValues.numberOfPeople,
        })
      );

      setExtraServiceForm(extraServiceFormData);
    }
  }, [initialValues?.data]);

  async function processExtraServiceRequests() {
    if (basketId) {
      const selectedExtraServices = extraServiceForm
        ?.filter((extraService) => extraService.quantity)
        ?.map(({ id, ...others }) => ({
          extraServiceId: id,
          ...others,
        }));

      for (let i = 0; i < selectedExtraServices.length; i++) {
        const extraService = selectedExtraServices[i];
        try {
          await callService(
            {
              service: 'PrebookService',
              method: 'prebookAddTourExtraService',
              version: 'v3',
            },
            {
              body: {
                prebookModel: {
                  tourId: initialValues.tourId,
                  startDate: initialValues.startDate,
                  endDate: initialValues.endDate,
                  numberOfPeople:
                    selectedExtraServiceItems?.find(
                      (item) =>
                        item.extraServiceId === extraService.extraServiceId
                    )?.quantity ?? extraService.numberOfPeople,
                  currency: initialValues?.currency,
                  saleDate: initialValues.saleDate,
                  tourPrice: initialValues.tourPrice,
                  extraServiceList: [extraService],
                },
                basketId,
                parentItemId: initialValues.itemId,
              },
            }
          );
        } catch (error) {
          console.error('Error adding extra service:', error);
          // Handle the error appropriately (e.g., stop processing, notify the user)
          break; // Stop processing further requests if one fails
        }
      }
    }
  }

  const addTourExtraService = async () => {
    await processExtraServiceRequests();
  };

  const { mutate, isLoading: isSubmittingForm } = useMutation(
    ['tour-prebook-extra-service'],
    addTourExtraService,
    {
      onSuccess: () => {
        onSuccess?.();
      },
    }
  );

  if (isDesktop === undefined) return null;
  return (
    <>
      {isDesktop ? (
        <TourExtraServiceFormDesktop
          onCancel={onCancel}
          initialValues={initialValues}
          extraServiceForm={extraServiceForm}
          refresh={refresh}
          setSelectedExtraServiceItems={setSelectedExtraServiceItems}
          isSubmittingForm={isSubmittingForm}
          mutate={mutate}
          selectedExtraServiceItems={selectedExtraServiceItems}
        />
      ) : (
        <TourExtraServiceFormMobile
          onCancel={onCancel}
          initialValues={initialValues}
          extraServiceForm={extraServiceForm}
          refresh={refresh}
          setSelectedExtraServiceItems={setSelectedExtraServiceItems}
          isSubmittingForm={isSubmittingForm}
          mutate={mutate}
          selectedExtraServiceItems={selectedExtraServiceItems}
        />
      )}
    </>
  );
};

export default TourExtraServiceForm;
